import { useRouter } from "next/router";
import { useEffect } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { selectHasFreeDeliveryApplied } from "../../../services/redux/selectors/loyaltyOfferSelectors";
import {
  removeItemAction,
  resetCartAction,
} from "../../../services/redux/slices/cartLoyaltyV2Slice";
import {
  setAddressSelectionDrawerState,
  setAnimatedGlobalBannerState,
  setBottomSheetState,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import {
  CUSTOMER_ANALYTICS_EVENT,
  DeliveryTypeDisplayName,
  OrderTypesEnum,
  PagesEnum,
} from "../../../utils/enums";
import { getAllDisabledItems } from "../../../utils/help/itemDetails/itemDetails";
import {
  getFranchiseDisplayName,
  localizedNumber,
} from "../../../utils/help/localization/localization";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import { numberWithCurrency } from "../../../utils/help/pricing/pricing";
import { formatTime } from "../../../utils/help/textPreparation/textPreparation";
import { Franchise, RestaurantTypeEnum } from "../../../utils/types";
import { ArrowIcon } from "../../screens/S-Cart/Cart.icons";
import { useDeliveryInfo } from "../../screens/S-Cart/DeliveryInfo/useDeliveryInfo";
import { ClockDelivery } from "../../screens/S-Category/Icons/icons";
import {
  BeachUmbrellaIcon,
  CarPickupIcon,
  DeliveryIcon,
  DineinIcon,
  PickupIcon,
} from "../Icons/Icons";
import styles from "./FranchiseSelector.module.scss";
import {
  selectCurrentDeliveryConfig,
  selectDeliveryFees,
  selectMaxEstimatedTime,
  selectMinEstimatedTime,
} from "../../../services/redux/selectors/deliverySelectors";

export const FranchiseSelector = (props: {
  setSelectedFranchise?: (franchise: Franchise) => void;
  selectedFranchise?: Franchise;
  setFranchiseOpened?: (opened: boolean) => void;
  franchiseOpened?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslationI18();
  const router = useRouter();
  const addCustomerAnalytics = useCustomerAnalytics();
  const { franchise, invalid_franchise } = router.query;
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const isBottomSheetOpen = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal.isOpen
  );
  const { handleChangeAddress } = useDeliveryInfo();

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);

  const delivery = useAppSelector((state) => state.delivery);
  const currentDeliveryConfig = useAppSelector(selectCurrentDeliveryConfig);
  const minEstimatedTime = useAppSelector(selectMinEstimatedTime);
  const maxEstimatedTime = useAppSelector(selectMaxEstimatedTime);
  const deliveryFees = useAppSelector(selectDeliveryFees);
  const selectedAddress = delivery.selectedAddress;

  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);

  const restaurantType = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );

  const currency = useAppSelector((state) => state.config.currency);
  const hasFreeDeliveryApplied = useAppSelector(selectHasFreeDeliveryApplied);

  const isEcommerce: boolean = restaurantType === RestaurantTypeEnum.ECOMMERCE;
  const customDeliveryTime = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  ).customDeliveryTime;
  const maxDeliveryTime = Math.max(maxEstimatedTime, customDeliveryTime);

  const getEstimatedTime = () => {
    let estimatedTime: string | null = null;
    if (currentDeliveryConfig || delivery.selectedAddress) {
      estimatedTime = isEcommerce
        ? formatTime(maxDeliveryTime)
        : `${localizedNumber(minEstimatedTime)}~${localizedNumber(
            maxDeliveryTime
          )} ${t("common:min")}`;
    }

    return estimatedTime;
  };

  const cart = cartLoyaltyV2.cart;

  const isCartPage = router.pathname.includes(PagesEnum.Cart);
  const isTipPage = router.pathname.includes(PagesEnum.Tip);

  const selectedFranchise = useAppSelector(
    (store) => store.config.selectedFranchise
  );

  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  const hasWelcomeDrawerV3 = useAppSelector(
    (state) => state.config.restaurantInfo?.config?.hasWelcomeDrawerV3
  );

  // Only relevant under hasWelcomeDrawerV3: that's where a deliveryV2 type
  // other than table-delivery (car pickup, beach delivery, ...) can exist
  // with usesMap incorrectly true in config data. Legacy restaurants have no
  // such ambiguity, so trust usesMap as-is there — some have a real
  // map-based delivery type not literally named "table-delivery".
  const isTableDeliveryConfig = hasWelcomeDrawerV3
    ? currentDeliveryConfig?.deliveryType.displayName ===
      DeliveryTypeDisplayName.TABLE_DELIVERY
    : Boolean(currentDeliveryConfig?.usesMap);

  const labelText = (() => {
    switch (orderTypeParam) {
      case OrderTypesEnum.TAKE_OUT:
        return t(
          "common:pick-up-from",
          {},
          router.locale,
          selectedFranchise?.franchiseConfig.takeOut?.customOrderTypeName,
          selectedFranchise?.franchiseConfig.takeOut
            ?.localizedCustomOrderTypeName
        );
      case OrderTypesEnum.DINE_IN:
        return t(
          "common:dine-in-at",
          {},
          router.locale,
          selectedFranchise?.franchiseConfig.dineIn?.customOrderTypeName,
          selectedFranchise?.franchiseConfig.dineIn
            ?.localizedCustomOrderTypeName
        );
      case OrderTypesEnum.DELIVERYV2:
        if (isTableDeliveryConfig) {
          return null;
        } else {
          const deliveryType = currentDeliveryConfig?.deliveryType;
          if (deliveryType?.displayName) {
            return t(`common:${deliveryType?.displayName}-at`);
          } else {
            return t("common:delivering-from");
          }
        }
    }
  })();

  const isHomePage = router.pathname.includes(`/${PagesEnum.Home}`);

  const isAddressMode =
    orderTypeParam === OrderTypesEnum.DELIVERYV2 && isTableDeliveryConfig;

  const displayFranchiseName = (franchise?: Franchise) => {
    return getFranchiseDisplayName(franchise);
  };

  const valueText = (() => {
    if (isAddressMode) {
      return selectedAddress?.addressTitle ?? t("delivery:select-address");
    } else {
      return displayFranchiseName(selectedFranchise || props.selectedFranchise);
    }
  })();

  const checkCartForDisabledItems = () => {
    const allDisabledItemsIds = getAllDisabledItems(
      itemStatusRedux,
      orderTypeParam
    );
    if (allDisabledItemsIds.length === 0) {
      return;
    }
    const disabledCartItemsIndexes: number[] = [];
    cart.items.forEach((item, index) => {
      if (allDisabledItemsIds.includes(item.itemDetails.id)) {
        disabledCartItemsIndexes.push(index);
      }
    });
    if (disabledCartItemsIndexes.length === 0) {
      return;
    } else if (router.pathname.includes("tip")) {
      return router.push(getPageLink(router, PagesEnum.Cart).href);
    }
    if (disabledCartItemsIndexes.length > 0) {
      dispatch(
        removeItemAction({
          itemIndex: disabledCartItemsIndexes,
        })
      );
      dispatch(
        setAnimatedGlobalBannerState({
          isOpen: true,
          text: t("cart:items-not-available"),
          warning: true,
        })
      );
    }
  };

  useEffect(() => {
    if (restaurantInfo?.franchises) {
      initialize();
    }
  }, [dispatch, selectedFranchise, restaurantInfo?.franchises]);

  useEffect(() => {
    if (props && (isCartPage || isTipPage)) {
      checkCartForDisabledItems();
    }
  }, [franchise, cart.cartItemsNumber, orderTypeParam]);

  const initialize = () => {
    if (invalid_franchise && invalid_franchise === "true") {
      dispatch(resetCartAction());
    }
  };

  const icon = () => {
    if (orderTypeParam === OrderTypesEnum.DINE_IN) {
      return <DineinIcon />;
    } else if (orderTypeParam === OrderTypesEnum.TAKE_OUT) {
      return <PickupIcon />;
    } else if (orderTypeParam === OrderTypesEnum.DELIVERYV2) {
      if (
        currentDeliveryConfig?.deliveryType?.displayName ===
        DeliveryTypeDisplayName.CAR_PICKUP
      ) {
        return <CarPickupIcon />;
      } else if (
        currentDeliveryConfig?.deliveryType?.displayName ===
        DeliveryTypeDisplayName.BEACH_DELIVERY
      ) {
        return <BeachUmbrellaIcon />;
      } else {
        return <DeliveryIcon />;
      }
    }
  };

  const handleSelectorClick = () => {
    if (hasWelcomeDrawerV3) {
      // Clicking the branch + order-type banner. Cart has its own event; every
      // other page carrying this banner (home/menu) reports the menu variant.
      addCustomerAnalytics(
        isCartPage
          ? CUSTOMER_ANALYTICS_EVENT.CLICKED_WELCOME_BANNER_IN_CART
          : CUSTOMER_ANALYTICS_EVENT.CLICKED_WELCOME_BANNER_ON_MENU
      );

      if (isHomePage) {
        const locationLink = getPageLink(router, PagesEnum.Location, {
          orderType: orderTypeParam as OrderTypesEnum,
        });
        router.push(locationLink.href, locationLink.as);
      } else {
        dispatch(setAddressSelectionDrawerState(true));
      }

      return;
    }

    // Non-V3: existing behavior unchanged
    if (isAddressMode) {
      handleChangeAddress();
    } else {
      dispatch(setBottomSheetState(true));
    }
  };

  return (
    <div
      className={`${styles.wrapper}`}
      onClick={() => {
        handleSelectorClick();
      }}
    >
      <div className={styles.icon_container}>{icon()}</div>
      {labelText && <div className={styles.select_label}>{labelText}:</div>}

      <div
        className={`${styles.select} ${isBottomSheetOpen && styles.active}`}
        data-testid={"franchise_location"}
      >
        <span className={`${styles.select_text}`}>{valueText}</span>

        <span className={styles.select_arrow_icon}>
          <ArrowIcon />
        </span>
      </div>

      {isAddressMode && selectedAddress && (
        <div className={styles.delivery_info}>
          <div className={styles.estimated_time}>
            <ClockDelivery />
            {getEstimatedTime()}
          </div>
          {hasFreeDeliveryApplied ? (
            <div
              className={`${styles.delivery_fees} ${styles.free_delivery}`}
              data-testid="franchise_selector_free_delivery"
            >
              {t("offers:free-delivery")}
            </div>
          ) : (
            // `!= null` rather than truthiness: selectDeliveryFees returns null
            // for "no fee known", so a zero fee is a real fee to show.
            deliveryFees != null && (
              <div className={styles.delivery_fees}>
                {numberWithCurrency(deliveryFees, currency)}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};
